import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import {
  GrantData,
  GrantListPage,
  GrantUserData,
  OrganizationWorkData,
  WorkData,
  WorkListPage,
  defaultGrantsPage,
  defaultWorksPage,
} from '../../types'
import { grantApi } from '../api/grantApi'
import { workApi } from '../api/workApi'

export interface GrantState {
  grantsPage: GrantListPage
  grant: GrantData | null
  grantUsers: GrantUserData[]
  grantWorks: OrganizationWorkData[]
  worksPage: WorkListPage
  work: OrganizationWorkData | null
  fileData: Record<string, Record<string, any>>
}

const initialState: GrantState = {
  grantsPage: defaultGrantsPage,
  grant: null,
  grantUsers: [],
  grantWorks: [],
  worksPage: defaultWorksPage,
  work: null,
  fileData: {},
}

export const grantSlice = createSlice({
  name: 'grant',
  initialState,
  reducers: {
    setGrantsPage: (state, action: PayloadAction<GrantListPage>) => {
      state.grantsPage = action.payload
    },
    setGrant: (state, action: PayloadAction<GrantData>) => {
      state.grant = action.payload
    },
    clearGrant: (state) => {
      state.grant = null
    },
    setWorksPage: (state, action: PayloadAction<WorkListPage>) => {
      state.worksPage = action.payload
    },
    setGrantWorks: (state, action: PayloadAction<OrganizationWorkData[]>) => {
      state.grantWorks = action.payload
    },
    setGrantUsers: (state, action: PayloadAction<GrantUserData[]>) => {
      state.grantUsers = action.payload
    },
    setWork: (state, action: PayloadAction<OrganizationWorkData>) => {
      state.work = action.payload
    },
    clearWork: (state) => {
      state.work = null
    },
    setFileData: (state, action: PayloadAction<{ id: string; data: Record<string, any> }>) => {
      state.fileData = {
        ...state.fileData,
        [action.payload.id]: action.payload.data,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(grantApi.endpoints.searchGrants.matchFulfilled, (state, { payload }) => {
      state.grantsPage = payload
    })

    builder.addMatcher(grantApi.endpoints.getGrant.matchFulfilled, (state, { payload }) => {
      state.grant = payload
    })

    builder.addMatcher(grantApi.endpoints.getGrantUsers.matchFulfilled, (state, { payload }) => {
      state.grantUsers = payload
    })

    builder.addMatcher(grantApi.endpoints.getGrantWorks.matchFulfilled, (state, { payload }) => {
      state.grantWorks = payload
    })

    builder.addMatcher(workApi.endpoints.searchWorks.matchFulfilled, (state, { payload }) => {
      state.worksPage = payload
    })

    builder.addMatcher(workApi.endpoints.getWork.matchFulfilled, (state, { payload }) => {
      state.work = payload
    })
  },
})

// Action creators are generated for each case reducer function
export const {
  setGrantsPage,
  setGrant,
  setGrantUsers,
  setWorksPage,
  clearWork,
  clearGrant,
  setGrantWorks,
  setFileData,
} = grantSlice.actions

export default grantSlice.reducer
